import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
  Box,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { bgBlur } from "../../../utils/cssStyles";
import Iconify from "../../../components/Iconify";
import Searchbar from "./Searchbar";
import AccountPopover from "./AccountPopover";
import LanguagePopover from "./LanguagePopover";
import NotificationsPopover from "./NotificationsPopover";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store";
import { setAppUser, setDarkTheme } from "../../../store/appUserReducer";
import { useTranslation } from "react-i18next";
import { toAddress } from "../../../utils/address";
import cloneDeep from "lodash/cloneDeep";
import { CURRENT_CLIENT } from "../../../constants";
import { AppUser } from "../../../pages/api";
import { setCookie } from "../../../utils/cookie";

const NAV_WIDTH = 280;
const CLOSE_NAV_WIDTH = 70;
const HEADER_MOBILE = 64;
// const HEADER_DESKTOP = 92;
const HEADER_DESKTOP = 64;

const StyledRoot = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "openNav",
})<{ openNav: boolean }>(
  ({ theme, openNav }) =>
    ({
      ...bgBlur({ color: theme.palette.background.default }),
      boxShadow: "none",
      [theme.breakpoints.up("lg")]: {
        width: `calc(100% - ${
          openNav ? NAV_WIDTH + 1 : CLOSE_NAV_WIDTH + 1
        }px)`,
      },
    }) as any,
);

const StyledToolbar = styled(Toolbar)(({ theme }: any) => ({
  minHeight: HEADER_MOBILE,
  [theme.breakpoints.up("lg")]: {
    minHeight: HEADER_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

export interface IHeader {
  openNav: boolean;
  onOpenNav: () => void;
}

export default function Header({ openNav, onOpenNav }: IHeader) {
  // Subscribe to the individual fields rather than the whole appUser slice,
  // then rebuild the object behind a memo. Unrelated slice writes no longer
  // re-render the header, and `appUser` stays referentially stable so
  // AccountPopover's useMemo([appUser]) does not recompute on every toast.
  const user = useSelector((state: RootState) => state.appUser.user);
  const language = useSelector((state: RootState) => state.appUser.language);
  const darkTheme = useSelector((state: RootState) => state.appUser.darkTheme);
  const appUser = useMemo<AppUser>(
    () => ({ user, language, darkTheme }),
    [user, language, darkTheme],
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onClientChange = (event: any) => {
    const client: any = appUser.user?.clients.find(
      (c: any) => c.id === event.target.value,
    );
    if (client?.id) {
      navigate("/");
      let updatedappUser: any = cloneDeep(appUser);
      if (updatedappUser?.user) {
        updatedappUser.user.currentClient = {
          ...client,
          country: client?.address ? toAddress(client?.address)?.country : "",
        };
        dispatch(setAppUser(updatedappUser));
        setCookie(CURRENT_CLIENT, client.id);
      }
      window.location.href = "/";
    }
  };

  return (
    <StyledRoot openNav={openNav}>
      <StyledToolbar>
        <IconButton
          onClick={onOpenNav}
          sx={{
            // mr: 1,
            color: "text.primary",
            display: { lg: "none" },
          }}
          size="small"
        >
          <Iconify icon="eva:menu-2-fill" width={20} />
        </IconButton>

        {appUser.user?.clients && appUser.user.clients.length > 1 && (
          <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
            <Select
              id="user-clients"
              value={appUser.user?.currentClient?.id || ""}
              label=""
              size="small"
              onChange={onClientChange}
            >
              {appUser.user?.clients
                .filter((c: any) => !c.isBranch)
                .map((c: any, i: number) => (
                  <MenuItem
                    key={`a-branch-${i}`}
                    value={c.id}
                    sx={{ fontWeight: "bold" }}
                  >
                    {t("MainAdminDashboard")}
                  </MenuItem>
                ))}
              {appUser.user.clients
                .filter((c: any) => c.isBranch)
                .map((c: any, i: number) => (
                  <MenuItem key={`branch-${i}`} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        <Searchbar />
        <Box sx={{ flexGrow: 1 }} />

        <Stack
          direction="row"
          alignItems="center"
          spacing={{
            xs: 0.5,
            sm: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => dispatch(setDarkTheme(!appUser.darkTheme))}
            title={String(!!appUser.darkTheme ? t("LightMode") : t("DarkMode"))}
            sx={{ color: "text.primary" }}
          >
            <Iconify
              icon={!!appUser.darkTheme ? "eva:moon-fill" : "noto:sun"}
              width={20}
            />
          </IconButton>
          <LanguagePopover />
          <NotificationsPopover />
          <AccountPopover appUser={appUser} />
        </Stack>
      </StyledToolbar>
    </StyledRoot>
  );
}
